/** @jsxImportSource @emotion/react */
'use memo'

import React, { createContext, useContext, useEffect, useReducer } from 'react'
import { useUsersMeSuspensions } from '@sportninja/common/hooks/useUsersMeSuspensions'
import { loadProfileItem } from '../../helpers/profileRequests'
import { useMeFamily } from '../../hooks/useMeFamily'
import { ProfileIdentifier } from '../../types'
import { useSelectedPlayer } from '../../hooks/useSelectedPlayer'
import { initialState, profileReducer } from './reducer'
import { ActionType, ProfileContextValue } from './types'

const ProfileContext = createContext<ProfileContextValue | undefined>(undefined)

interface ProfileProviderProps {
  children: React.ReactNode
  selfUserId?: ProfileIdentifier
}

export const ProfileProvider: React.FC<ProfileProviderProps> = ({
  children,
  selfUserId,
}) => {
  const [state, dispatch] = useReducer(profileReducer, initialState)
  const { selectedPlayer, selectedPlayerId, selectPlayer } = useSelectedPlayer(
    state.players,
    state.loadedPlayers
  )
  const {
    data: suspensions,
    loading: suspensionLoading,
    isSuspended,
    refetch,
  } = useUsersMeSuspensions(false)

  const {
    players: familyPlayers,
    isLoading: familyLoading,
    error: familyError,
    refetch: refetchFamily,
  } = useMeFamily()

  useEffect(() => {
    loadProfileItem(dispatch).catch(() => undefined)
    refetch().catch(() => undefined)
  }, [refetch, selfUserId])

  // Sync the me/family query result into the reducer the rest of the profile
  // surface reads from (players, selfPlayerIds, showStats, loadedPlayers).
  useEffect(() => {
    if (familyLoading) {
      return
    }
    if (familyError) {
      dispatch({ type: ActionType.SET_LOADED_PLAYERS, payload: true })
      return
    }
    const selfPlayerIds = [
      ...new Set<ProfileIdentifier>(familyPlayers.map((player) => player.id)),
    ]
    dispatch({
      type: ActionType.SET_PLAYERS,
      payload: {
        players: familyPlayers,
        selfPlayerIds,
        showStats: familyPlayers.length > 0,
      },
    })
  }, [familyPlayers, familyLoading, familyError])

  const value: ProfileContextValue = {
    ...state,
    suspensions,
    suspensionLoading,
    isSuspended,
    readItem: async (forceApi = false) => {
      await loadProfileItem(dispatch, forceApi)
    },
    loadPlayers: async () => {
      await refetchFamily()
    },
    refreshSuspensions: async () => {
      await refetch()
    },
    setShowStats: (nextShowStats) => {
      dispatch({ type: ActionType.SET_SHOW_STATS, payload: nextShowStats })
    },
    openNotificationSettings: () => {
      dispatch({
        type: ActionType.SET_NOTIFICATION_SETTINGS_OPEN,
        payload: true,
      })
    },
    closeNotificationSettings: () => {
      dispatch({
        type: ActionType.SET_NOTIFICATION_SETTINGS_OPEN,
        payload: false,
      })
    },
    openEditModal: () => {
      dispatch({ type: ActionType.SET_EDIT_OPEN, payload: true })
    },
    closeEditModal: () => {
      dispatch({ type: ActionType.SET_EDIT_OPEN, payload: false })
    },
    selectedPlayer,
    selectedPlayerId,
    selectPlayer,
    // Primary (admin) for the selected player? The record comes from
    // /users/me/family via selectedPlayer, which carries `is_primary`.
    isPrimaryForSelectedPlayer: Boolean(selectedPlayer?.is_primary),
  }

  return (
    <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>
  )
}

export const useProfileContext = (): ProfileContextValue => {
  const context = useContext(ProfileContext)

  if (!context) {
    throw new Error('useProfileContext must be used within a ProfileProvider')
  }

  return context
}
